import { useState, useEffect } from 'react';
import { useAuth } from 'context/AuthContext';
import usePageTitle from 'hooks/usePageTitle';
import { useAlertCount } from 'components/shared/NotificationPanel';
import api from 'services/api';

export default function Utilisateurs() {
  const { user: currentUser } = useAuth();
  const alertCount = useAlertCount();
  usePageTitle('Utilisateurs', alertCount);

  const [users, setUsers] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editRoleIds, setEditRoleIds] = useState([]);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  /* ⛔ PLUS DE CHAMP `password` — RETIRÉ LE 12/10/2026, ET C'ÉTAIT UNE PANNE.
   * ═════════════════════════════════════════════════════════════════════════
   * 🔴 MESURÉ EN PRODUCTION LE 12/10 : l'écran EXIGEAIT un mot de passe
   *    (« Nom, email et mot de passe requis ») et le serveur le REFUSAIT depuis
   *    le 17/09 (`400 MOT_DE_PASSE_NON_ACCEPTE`). Sans mot de passe l'écran
   *    bloquait, avec mot de passe le serveur refusait : **aucun compte ne
   *    pouvait plus être créé.** Les deux bouts se refusaient mutuellement, et
   *    chacun avait raison de son côté.
   * ➡️ Le porteur choisit lui-même son mot de passe en ouvrant le lien
   *    d'activation que la création rend désormais — une seule fois.
   *
   * ⓘ `est_salarie` DÉMARRE À `null`, ET C'EST TOUT L'OBJET : trois états — oui,
   *   non, et PAS ENCORE RÉPONDU. Un défaut à `false` aurait répondu à la place
   *   de l'employeur, et la liste des comptes sans fiche serait restée vide. */
  const [createForm, setCreateForm] = useState({
    nom: '', prenom: '', email: '', telephone: '', role_ids: [],
    est_salarie: null, motif_qualification: '',
  });
  const [resetUserId, setResetUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  /* Le lien d'activation rendu par le serveur — affiché UNE FOIS, jamais relu. */
  const [lienEmis, setLienEmis] = useState(null);
  /* Les comptes sur lesquels personne ne s'est encore prononcé. */
  const [sansFiche, setSansFiche] = useState([]);
  const [qualifierCompte, setQualifierCompte] = useState(null);
  const [qualifForm, setQualifForm] = useState({ est_salarie: null, motif: '' });
  const [qualifErreur, setQualifErreur] = useState('');

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleForm, setRoleForm] = useState({ nom: '' });
  const [roleError, setRoleError] = useState('');
  const [savingRole, setSavingRole] = useState(false);
  // ⚠️ La liste vient du SERVEUR (`/users/fonctions`), pas d'une copie ici :
  //    c'est le referentiel serveur qui fait foi pour `authorize()`. Une copie
  //    cote client divergerait au premier ajout.
  const [fonctions, setFonctions] = useState([]);

  const ouvrirModaleRole = async () => {
    setRoleError('');
    setRoleForm({ nom: '' });
    try {
      const r = await api.get('/users/fonctions');
      setFonctions(r.data);
    } catch { setFonctions([]); }
    setShowRoleModal(true);
  };

  // ⚠️ Le message d'erreur vient du SERVEUR et il NOMME le probleme (fonction
  //    deja definie, fonction non reconnue). Aucune condition metier ne sort en
  //    500 ni en message generique.
  // ⚠️ Le LIBELLE n'est plus envoye : il vient du referentiel serveur. Deux
  //    tenants ne doivent pas donner deux sens differents a la meme fonction.
  const handleCreateRole = async (e) => {
    e.preventDefault();
    setRoleError('');
    setSavingRole(true);
    try {
      await api.post('/users/roles', { nom: roleForm.nom });
      const r = await api.get('/users/roles');
      setAllRoles(r.data);
      setShowRoleModal(false);
      setRoleForm({ nom: '' });
    } catch (err) {
      setRoleError(err.response?.data?.error || 'Création du rôle impossible');
    } finally { setSavingRole(false); }
  };

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      api.get('/users'),
      api.get('/users/roles'),
      /* ⓘ Un échec ici ne doit pas vider l'écran : la liste des comptes non
       *   qualifiés est une information de plus, pas la page. */
      api.get('/qualification-comptes/comptes-sans-fiche').catch(() => ({ data: { comptes: [] } })),
    ]).then(([usersRes, rolesRes, sansFicheRes]) => {
      setUsers(usersRes.data);
      setAllRoles(rolesRes.data);
      setSansFiche((sansFicheRes.data && sansFicheRes.data.comptes) || []);
    }).catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, []);

  const openEdit = (u) => {
    setEditingUser(u);
    setEditForm({ nom: u.nom, prenom: u.prenom, email: u.email, telephone: u.telephone || '', actif: u.actif });
    setEditRoleIds(u.roles.map(r => r.id));
  };

  const toggleRole = (roleId) => {
    setEditRoleIds(prev =>
      prev.includes(roleId) ? prev.filter(id => id !== roleId) : [...prev, roleId]
    );
  };

  const toggleCreateRole = (roleId) => {
    setCreateForm(f => ({
      ...f,
      role_ids: f.role_ids.includes(roleId) ? f.role_ids.filter(id => id !== roleId) : [...f.role_ids, roleId],
    }));
  };

  const handleSave = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      const res = await api.put(`/users/${editingUser.id}`, {
        ...editForm,
        role_ids: editRoleIds,
      });
      setUsers(prev => prev.map(u => u.id === editingUser.id ? { ...u, ...res.data } : u));
      setEditingUser(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
    setSaving(false);
  };

  /* ⛔ `validatePw` A ÉTÉ RETIRÉE AVEC LE CHAMP QU'ELLE GARDAIT. La politique de
   *    mot de passe vit côté serveur (`utils/passwordPolicy`), où l'écran
   *    d'activation la rencontre. *Une copie des règles dans un écran qui ne
   *    pose plus de mot de passe serait une règle sans objet, prête à diverger.* */

  const handleCreate = async () => {
    if (!createForm.nom || !createForm.email) return alert('Nom et email requis');
    /* ⛔ LA QUESTION SE POSE AVANT L'ÉCRITURE, PAS APRÈS. Le serveur la refuse
     *    aussi (`QUALIFICATION_NON_REPONDUE`) : ce contrôle-ci évite un
     *    aller-retour, il ne remplace pas le garde. */
    if (createForm.est_salarie === null) {
      return alert('Répondre à la question : ce compte correspond-il à un salarié de la société ?');
    }
    if (!createForm.motif_qualification.trim()) {
      return alert('Le motif est obligatoire : dire pourquoi ce compte correspond, ou non, à un salarié.');
    }
    setSaving(true);
    try {
      const res = await api.post('/users', createForm);
      setShowCreate(false);
      setCreateForm({
        nom: '', prenom: '', email: '', telephone: '', role_ids: [],
        est_salarie: null, motif_qualification: '',
      });
      /* ⚠️ LE LIEN NE SERA PLUS JAMAIS AFFICHÉ, et c'est le serveur qui le dit.
       *    On le montre donc dans une fenêtre que l'administrateur ferme
       *    lui-même — jamais dans une alerte qui disparaît au clic suivant. */
      if (res.data && res.data.activation) {
        setLienEmis({
          titre: `Compte créé — ${res.data.prenom || ''} ${res.data.nom}`.trim(),
          message: res.data.message,
          ...res.data.activation,
        });
      }
      loadAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
    setSaving(false);
  };

  /* ═══════════════════════════════════════════════════════════════════════
   * RÉÉMETTRE UN LIEN — CE QUI REMPLACE LA RÉINITIALISATION
   * ═══════════════════════════════════════════════════════════════════════
   * 🔴 CET ÉCRAN POSTAIT ENCORE `new_password`, et le serveur le refusait depuis
   *    le 17/09 : la réinitialisation était donc MORTE elle aussi, comme la
   *    création. ➡️ L'administrateur redonne l'ACCÈS ; il ne choisit plus le
   *    secret de quelqu'un d'autre.
   * ⓘ « Deux liens vivants pour un même compte n'existent jamais » (Dom) : la
   *   réémission tue le précédent, et c'est la fonction SQL qui le fait.
   */
  const handleReemettreLien = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/users/${resetUserId}/reset-password`, {});
      const cible = users.find((u) => u.id === resetUserId);
      setResetUserId(null);
      if (res.data && res.data.activation) {
        setLienEmis({
          titre: `Nouvel accès — ${cible ? `${cible.prenom || ''} ${cible.nom}`.trim() : 'compte'}`,
          message: res.data.message,
          ...res.data.activation,
        });
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
    setSaving(false);
  };

  /* ── QUALIFIER UN COMPTE DÉJÀ EN PLACE ──────────────────────────────────
   * ⓘ Les comptes créés avant que la question existe n'ont pas de réponse. On
   *   ne la devine pas : on la demande, compte par compte. */
  const handleQualifier = async () => {
    if (qualifForm.est_salarie === null) {
      setQualifErreur('Répondre à la question.'); return;
    }
    if (!qualifForm.motif.trim()) {
      setQualifErreur('Le motif est obligatoire.'); return;
    }
    setSaving(true); setQualifErreur('');
    try {
      await api.post(`/qualification-comptes/comptes/${qualifierCompte.id}`, {
        est_salarie: qualifForm.est_salarie, motif: qualifForm.motif.trim(),
      });
      setQualifierCompte(null);
      setQualifForm({ est_salarie: null, motif: '' });
      loadAll();
    } catch (err) {
      setQualifErreur(err.response?.data?.error || 'Erreur');
    }
    setSaving(false);
  };

  const toggleActif = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { actif: !u.actif });
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, actif: !x.actif } : x));
    } catch {}
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';
  const formatConnexion = (d) => {
    if (!d) return 'Jamais';
    const diff = Date.now() - new Date(d).getTime();
    if (diff < 900000) return 'En ligne';
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)}h`;
    return new Date(d).toLocaleDateString('fr-FR');
  };

  const filtered = users.filter(u => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return `${u.prenom} ${u.nom} ${u.email}`.toLowerCase().includes(s);
  });

  if (loading) return <div style={{ padding: '2rem', color: 'var(--gss-text-secondary)' }}>Chargement...</div>;

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <input className="gss-input" placeholder="Rechercher un utilisateur..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ maxWidth: 280 }} />
        <div style={{ display: 'flex', gap: 8 }}>
          {/* ⚠️ CE BOUTON ETAIT DANS LA MODALE DE CREATION D'UTILISATEUR — donc
              introuvable : il fallait faire semblant de creer un utilisateur
              pour creer un role. Un geste de CONFIGURATION ne se cache pas dans
              un formulaire de SAISIE. Sorti sur l'ecran principal le 31/08. */}
          <button className="gss-btn" onClick={ouvrirModaleRole}>
            + Créer un rôle
          </button>
          <button className="gss-btn gss-btn-primary" onClick={() => setShowCreate(true)}>
            + Créer un utilisateur
          </button>
        </div>
      </div>

      {/* ⚠️ AUCUNE MENTION D'ETAT D'AVANCEMENT ICI — RETIREE le 31/08/2026.
          LE PRODUIT NE DOCUMENTE PAS SES PROPRES FAIBLESSES DE SECURITE A
          L'ECRAN. La version precedente enumerait les noms de roles reconnus et
          declarait quels ecrans restaient ouverts : c'etait une DIVULGATION, pas
          de la transparence. Un client y lisait que le produit n'a pas de
          controle d'acces ; un employe mal intentionne y lisait une carte.
          L'etat d'avancement vit dans le registre des chantiers (CLAUDE.md,
          §MODELE DE PERMISSIONS), jamais dans l'interface d'un client. */}

      {/* ══ COMPTES SANS FICHE SALARIÉ ════════════════════════════════════════
          ⓘ Les comptes créés avant que la question existe n'ont pas de réponse,
            et on ne la devine pas. Ce bloc DISPARAÎT quand la liste est vide :
            un encart permanent affichant « rien à faire » est un encart qu'on
            cesse de lire. */}
      {sansFiche.length > 0 && (
        <div className="content-card" style={{ marginBottom: 16, border: '2px solid #B45309' }}>
          <div className="content-card-title" style={{ marginBottom: 8, color: '#B45309' }}>
            Comptes sans fiche salarié ({sansFiche.length})
          </div>
          <div style={{ fontSize: 13, color: 'var(--gss-text-secondary)', marginBottom: 10 }}>
            Personne ne s&rsquo;est encore prononcé sur ces comptes. Un compte
            n&rsquo;est pas un salarié : tant que la question n&rsquo;est pas
            tranchée, aucune fiche n&rsquo;existe, et la personne n&rsquo;entre ni
            dans l&rsquo;effectif ni dans la paie.
          </div>
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr><th>Compte</th><th>Créé le</th><th style={{ width: 200 }}>Décision</th></tr>
              </thead>
              <tbody>
                {sansFiche.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <strong>{c.prenom} {c.nom}</strong>
                      <div style={{ color: 'var(--gss-text-secondary)', fontSize: '0.85rem' }}>{c.email}</div>
                    </td>
                    <td style={{ color: 'var(--gss-text-secondary)' }}>{formatDate(c.created_at)}</td>
                    <td>
                      <button className="gss-btn-sm" onClick={() => {
                        setQualifierCompte(c);
                        setQualifForm({ est_salarie: null, motif: '' });
                        setQualifErreur('');
                      }}>
                        Répondre à la question
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Rôles définis pour cette société */}
      <div className="content-card" style={{ marginBottom: 16 }}>
        <div className="content-card-title" style={{ marginBottom: 8 }}>
          Rôles de la société ({allRoles.length})
        </div>
        {allRoles.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--gss-text-secondary)' }}>
            Aucun rôle n'est défini. Créez-en un avant d'affecter un utilisateur.
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {allRoles.map(r => (
              <span key={r.id} className="gss-badge" style={{ fontSize: 12 }}>
                {r.description || r.nom}<span style={{ opacity: 0.6 }}> · {r.nom}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="content-card">
        <div className="gss-table-wrap">
          <table className="gss-table">
            <thead>
              <tr>
                <th>Utilisateur</th>
                <th>Rôles</th>
                <th>Statut</th>
                <th>Dernière connexion</th>
                <th>Créé le</th>
                <th style={{ width: 160 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aucun utilisateur trouvé</td></tr>
              )}
              {filtered.map(u => (
                <tr key={u.id}>
                  <td>
                    <strong>{u.prenom} {u.nom}</strong>
                    <div style={{ color: 'var(--gss-text-secondary)', fontSize: '0.85rem' }}>{u.email}</div>
                    {u.telephone && <div style={{ color: 'var(--gss-text-light)', fontSize: '0.8rem' }}>{u.telephone}</div>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {u.roles && u.roles.length > 0 ? u.roles.map(r => (
                        <span key={r.id} className="gss-badge" style={{ background: 'var(--gss-red-glow)', color: 'var(--gss-red)', fontSize: '0.75rem' }}>
                          {r.description || r.nom}
                        </span>
                      )) : (
                        <span style={{ color: 'var(--gss-text-light)' }}>Aucun rôle</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="gss-badge" style={{
                      background: u.actif ? 'rgba(26,135,84,0.08)' : 'rgba(220,38,38,0.08)',
                      color: u.actif ? '#1A8754' : '#DC2626'
                    }}>
                      {u.actif ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td style={{ color: 'var(--gss-text-secondary)' }}>{formatConnexion(u.derniere_connexion)}</td>
                  <td style={{ color: 'var(--gss-text-secondary)' }}>{formatDate(u.created_at)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <button className="gss-btn-icon" onClick={() => openEdit(u)} title="Modifier">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                      <button className="gss-btn-icon" onClick={() => setResetUserId(u.id)} title="Redonner l’accès (émet un lien d’activation)">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                      </button>
                      {u.id !== currentUser?.id && (
                        <button className="gss-btn-sm" style={{
                          background: u.actif ? 'rgba(220,38,38,0.06)' : 'rgba(26,135,84,0.06)',
                          color: u.actif ? '#DC2626' : '#1A8754',
                          borderColor: u.actif ? '#DC2626' : '#1A8754'
                        }} onClick={() => toggleActif(u)}>
                          {u.actif ? 'Désactiver' : 'Activer'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Créer utilisateur */}
      {/* ── MODALE : CREER UN ROLE ────────────────────────────────────────── */}
      {showRoleModal && (
        <div className="gss-modal-overlay" onClick={() => setShowRoleModal(false)}>
          <div className="gss-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="gss-modal-header">
              <div>
                <div className="gss-modal-title">Créer un rôle</div>
                <div className="gss-modal-subtitle">Le vocabulaire de votre société</div>
              </div>
              <button className="gss-modal-close" onClick={() => setShowRoleModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreateRole}>
              {/* ⚠️ NOM LIBRE RETIRE LE 31/08/2026, LE JOUR MEME DE SA LIVRAISON.
                  Il violait la regle : UN CHAMP NON CABLE NE S'EXPOSE PAS A LA
                  SAISIE. Mesure en production : un client avait cree
                  `responsable_ressources_humaines` — un nom juste, dans SON
                  vocabulaire — et son titulaire s'est retrouve devant un menu
                  VIDE, ni `authorize()` ni `SIDEBAR_SECTIONS` ne lisant un nom
                  hors des 12 reconnus.
                  ➡️ On CHOISIT desormais parmi les fonctions reellement
                     reconnues : aucune donnee inerte, aucun concept nouveau, et
                     la ligne d'avertissement devient inutile — l'ecran ne
                     promet plus rien qu'il ne tient.
                  ⚠️ MESURE D'ATTENTE : ce selecteur redevient un champ libre le
                     jour ou `authorize()` et la sidebar lisent des PERMISSIONS. */}
              <div className="gss-modal-body">
                {roleError && <div className="gss-alert gss-alert-error">{roleError}</div>}
                <div className="gss-form-group">
                  <label className="gss-label">Fonction *</label>
                  <select className="gss-input" value={roleForm.nom} autoFocus
                    onChange={e => setRoleForm(f => ({ ...f, nom: e.target.value }))}>
                    <option value="">— Choisir une fonction —</option>
                    {fonctions.map(f => (
                      <option key={f.nom} value={f.nom} disabled={f.deja_definie}>
                        {f.libelle}{f.deja_definie ? ' (déjà définie)' : ''}
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: 11.5, color: 'var(--gss-text-light)', marginTop: 6, lineHeight: 1.5 }}>
                    Les collaborateurs portant cette fonction accèderont aux écrans correspondants.
                  </div>
                </div>
              </div>
              <div className="gss-modal-footer">
                <button type="button" className="gss-btn" onClick={() => setShowRoleModal(false)}>Annuler</button>
                <button type="submit" className="gss-btn gss-btn-primary" disabled={savingRole || !roleForm.nom.trim()}>
                  {savingRole ? 'Création...' : 'Créer le rôle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="gss-modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="gss-modal-header">
              <h2>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
                <div>
                  Créer un utilisateur
                  <div className="gss-modal-subtitle">Nouveau compte utilisateur GSS-360</div>
                </div>
              </h2>
              <button className="gss-modal-close" onClick={() => setShowCreate(false)}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                <span>IDENTITÉ</span>
              </div>
              <div className="gss-form-grid">
                <div className="gss-field">
                  <label className="gss-label">Prénom</label>
                  <input className="gss-input" value={createForm.prenom} onChange={e => setCreateForm(f => ({ ...f, prenom: e.target.value }))} />
                </div>
                <div className="gss-field">
                  <label className="gss-label">Nom *</label>
                  <input className="gss-input" value={createForm.nom} onChange={e => setCreateForm(f => ({ ...f, nom: e.target.value }))} />
                </div>
              </div>

              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                <span>ACCÈS</span>
              </div>
              <div className="gss-form-grid">
                <div className="gss-field">
                  <label className="gss-label">Email *</label>
                  <input className="gss-input" type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} />
                </div>
                <div className="gss-field">
                  <label className="gss-label">Téléphone</label>
                  <input className="gss-input" value={createForm.telephone} onChange={e => setCreateForm(f => ({ ...f, telephone: e.target.value }))} />
                </div>
              </div>
              {/* ⓘ On DIT ce qui remplace le mot de passe, plutôt que de laisser
                  un vide inexpliqué là où un champ se trouvait. */}
              <div style={{ fontSize: 12, color: 'var(--gss-text-secondary)', marginTop: 8 }}>
                Aucun mot de passe ne se pose ici : la création émet un lien
                d&rsquo;activation, et son porteur choisit lui-même son mot de passe.
              </div>

              {/* ══ LA QUESTION — ELLE N'A PAS DE RÉPONSE PAR DÉFAUT ══════════
                  ⛔ ET ELLE PORTE SUR LE COMPTE, PAS SUR LA PERSONNE (Dom,
                     12/10/2026). La formulation précédente — « X est-elle
                     salariée ? » — accordait au féminin un genre que **le
                     système ne connaît pas** : `gladiator_sec.utilisateurs` ne
                     porte aucune colonne de civilité, et `agents.sexe` n'existe
                     qu'après la validation, s'il est renseigné.
                  ⓘ « Ce compte correspond-il à un salarié ? » est juste pour
                    tout le monde, et dit mieux la vraie question : on qualifie
                    un ACCÈS, on ne décrit pas quelqu'un. */}
              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/></svg>
                <span>CE COMPTE CORRESPOND-IL À UN SALARIÉ DE LA SOCIÉTÉ ?</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--gss-text-secondary)', marginBottom: 8 }}>
                Un compte n&rsquo;est pas un salarié : un cabinet externe, un
                consultant ou un compte de test ont un accès et pas de bulletin.
                Le produit ne le déduit ni du rôle, ni de l&rsquo;adresse e-mail.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { v: true, t: 'Oui, créer la fiche « à valider »', aide: 'Les ressources humaines la complètent et la valident.' },
                  { v: false, t: 'Non, compte d’accès seulement', aide: 'Aucune fiche. La décision est tracée avec son motif.' },
                ].map((o) => {
                  const choisi = createForm.est_salarie === o.v;
                  return (
                    <button key={String(o.v)} type="button" title={o.aide}
                      onClick={() => setCreateForm(f => ({ ...f, est_salarie: o.v }))}
                      style={{
                        padding: '8px 16px', borderRadius: 6, textAlign: 'left',
                        border: choisi ? '2px solid #8B1A1A' : '1px solid var(--gss-border-strong)',
                        background: choisi ? 'rgba(139,26,26,0.08)' : 'var(--gss-white)',
                        color: choisi ? '#8B1A1A' : 'var(--gss-text-secondary)',
                        fontWeight: choisi ? 700 : 400, fontSize: '0.85rem', cursor: 'pointer',
                      }}>
                      {choisi ? '✓ ' : ''}{o.t}
                      <div style={{ fontWeight: 400, fontSize: 11, opacity: 0.8 }}>{o.aide}</div>
                    </button>
                  );
                })}
              </div>
              <div className="gss-field" style={{ marginTop: 10 }}>
                <label className="gss-label">Motif *</label>
                <input className="gss-input" value={createForm.motif_qualification}
                  placeholder="Pourquoi ce compte correspond, ou non, à un salarié"
                  onChange={e => setCreateForm(f => ({ ...f, motif_qualification: e.target.value }))} />
              </div>

              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                <span>RÔLES</span>
              </div>
              {allRoles.length === 0 && (
                <div className="gss-alert gss-alert-warning" style={{ marginTop: 8, fontSize: 12 }}>
                  Aucun rôle n'est défini pour cette société. Créez-en un avant d'affecter un utilisateur.
                </div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {allRoles.map(r => {
                  const selected = createForm.role_ids.includes(r.id);
                  return (
                    <button key={r.id} type="button" onClick={() => toggleCreateRole(r.id)} style={{
                      padding: '6px 14px', borderRadius: 6,
                      border: selected ? '2px solid #8B1A1A' : '1px solid var(--gss-border-strong)',
                      background: selected ? 'rgba(139,26,26,0.08)' : 'var(--gss-white)',
                      color: selected ? '#8B1A1A' : 'var(--gss-text-secondary)',
                      fontWeight: selected ? 700 : 400, fontSize: '0.85rem', cursor: 'pointer',
                    }}>
                      {selected ? '✓ ' : ''}{r.description || r.nom}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="gss-modal-actions">
              <button className="gss-btn-cancel" onClick={() => setShowCreate(false)}>Annuler</button>
              <button className="gss-btn-deploy" onClick={handleCreate} disabled={saving}>
                {saving ? 'Création...' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Créer l'utilisateur</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifier utilisateur */}
      {editingUser && (
        <div className="gss-modal-overlay" onClick={() => setEditingUser(null)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="gss-modal-header">
              <h2>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                <div>
                  Modifier — {editingUser.prenom} {editingUser.nom}
                  <div className="gss-modal-subtitle">Gestion du compte utilisateur</div>
                </div>
              </h2>
              <button className="gss-modal-close" onClick={() => setEditingUser(null)}>&times;</button>
            </div>

            <div style={{ padding: '1.5rem' }}>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
                <span>IDENTITÉ</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div className="gss-field">
                  <label className="gss-label">Prénom</label>
                  <input className="gss-input" value={editForm.prenom || ''} onChange={e => setEditForm(f => ({ ...f, prenom: e.target.value }))} />
                </div>
                <div className="gss-field">
                  <label className="gss-label">Nom</label>
                  <input className="gss-input" value={editForm.nom || ''} onChange={e => setEditForm(f => ({ ...f, nom: e.target.value }))} />
                </div>
              </div>

              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <span>COORDONNÉES</span>
              </div>
              <div className="gss-field">
                <label className="gss-label">Email</label>
                <input className="gss-input" type="email" value={editForm.email || ''} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div className="gss-field">
                <label className="gss-label">Téléphone</label>
                <input className="gss-input" value={editForm.telephone || ''} onChange={e => setEditForm(f => ({ ...f, telephone: e.target.value }))} />
              </div>

              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                <span>RÔLES & PERMISSIONS</span>
              </div>
              <div className="gss-field">
                <label className="gss-label" style={{ marginBottom: '0.5rem', display: 'block' }}>Rôles attribués</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {allRoles.map(r => {
                    const selected = editRoleIds.includes(r.id);
                    return (
                      <button key={r.id} type="button" onClick={() => toggleRole(r.id)} style={{
                        padding: '6px 14px',
                        borderRadius: '6px',
                        border: selected ? '2px solid #8B1A1A' : '1px solid var(--gss-border-strong)',
                        background: selected ? 'rgba(139,26,26,0.08)' : 'var(--gss-white)',
                        color: selected ? '#8B1A1A' : 'var(--gss-text-secondary)',
                        fontWeight: selected ? 700 : 400,
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}>
                        {selected ? '✓ ' : ''}{r.description || r.nom}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="gss-modal-actions">
              <button className="gss-btn-cancel" onClick={() => setEditingUser(null)}>Annuler</button>
              <button className="gss-btn-deploy" onClick={handleSave} disabled={saving}>
                {saving ? 'Enregistrement...' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22L11 13L2 9L22 2Z"/></svg> Enregistrer</>}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ MODALE : RÉÉMETTRE UN LIEN D'ACCÈS ═══════════════════════════════
          ⛔ ELLE NE POSE PLUS DE MOT DE PASSE. Elle en posait un, et le serveur
             le refusait depuis le 17/09 : le geste était mort. L'administrateur
             redonne l'accès, il ne choisit plus le secret d'un autre. */}
      {resetUserId && (
        <div className="gss-modal-overlay" onClick={() => setResetUserId(null)}>
          <div className="gss-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="gss-modal-header">
              <h2>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                <div>
                  Redonner l&rsquo;accès
                  <div className="gss-modal-subtitle">{users.find(u => u.id === resetUserId)?.email}</div>
                </div>
              </h2>
              <button className="gss-modal-close" onClick={() => setResetUserId(null)}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem', fontSize: 13, lineHeight: 1.6 }}>
              Un lien d&rsquo;activation va être émis. Son porteur choisira
              lui-même son mot de passe : vous ne le connaîtrez pas.
              <div style={{ marginTop: 10, color: 'var(--gss-text-secondary)' }}>
                S&rsquo;il existait déjà un lien vivant pour ce compte, il devient
                mort à l&rsquo;instant : un compte n&rsquo;a jamais deux liens valides.
              </div>
            </div>
            <div className="gss-modal-actions">
              <button className="gss-btn-cancel" onClick={() => setResetUserId(null)}>Annuler</button>
              <button className="gss-btn-deploy" onClick={handleReemettreLien} disabled={saving}>
                {saving ? 'Émission...' : 'Émettre le lien'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ MODALE : QUALIFIER UN COMPTE DÉJÀ EN PLACE ════════════════════════ */}
      {qualifierCompte && (
        <div className="gss-modal-overlay" onClick={() => setQualifierCompte(null)}>
          <div className="gss-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="gss-modal-header">
              <h2>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                <div>
                  Ce compte correspond-il à un salarié de la société ?
                  <div className="gss-modal-subtitle">{qualifierCompte.email}</div>
                </div>
              </h2>
              <button className="gss-modal-close" onClick={() => setQualifierCompte(null)}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              {qualifErreur && (
                <div className="gss-alert gss-alert-warning" style={{ marginBottom: 10, fontSize: 12 }}>
                  {qualifErreur}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { v: true, t: 'Oui, créer la fiche « à valider »', aide: 'Les ressources humaines la complètent et la valident.' },
                  { v: false, t: 'Non, compte d’accès seulement', aide: 'Aucune fiche. La décision est tracée avec son motif.' },
                ].map((o) => {
                  const choisi = qualifForm.est_salarie === o.v;
                  return (
                    <button key={String(o.v)} type="button" title={o.aide}
                      onClick={() => setQualifForm(f => ({ ...f, est_salarie: o.v }))}
                      style={{
                        padding: '8px 16px', borderRadius: 6, textAlign: 'left',
                        border: choisi ? '2px solid #8B1A1A' : '1px solid var(--gss-border-strong)',
                        background: choisi ? 'rgba(139,26,26,0.08)' : 'var(--gss-white)',
                        color: choisi ? '#8B1A1A' : 'var(--gss-text-secondary)',
                        fontWeight: choisi ? 700 : 400, fontSize: '0.85rem', cursor: 'pointer',
                      }}>
                      {choisi ? '✓ ' : ''}{o.t}
                      <div style={{ fontWeight: 400, fontSize: 11, opacity: 0.8 }}>{o.aide}</div>
                    </button>
                  );
                })}
              </div>
              <div className="gss-field" style={{ marginTop: 12 }}>
                <label className="gss-label">Motif *</label>
                <input className="gss-input" value={qualifForm.motif}
                  placeholder="Pourquoi ce compte correspond, ou non, à un salarié"
                  onChange={e => setQualifForm(f => ({ ...f, motif: e.target.value }))} />
              </div>
              {/* ⓘ La réversibilité se DIT au moment de décider, pas après. */}
              <div style={{ fontSize: 12, color: 'var(--gss-text-secondary)', marginTop: 10 }}>
                Cette décision se clôture plus tard, avec un motif, et le compte
                reparaît alors dans cette liste. Rien ne s&rsquo;efface.
              </div>
            </div>
            <div className="gss-modal-actions">
              <button className="gss-btn-cancel" onClick={() => setQualifierCompte(null)}>Annuler</button>
              <button className="gss-btn-deploy" onClick={handleQualifier} disabled={saving}>
                {saving ? 'Enregistrement...' : 'Enregistrer la décision'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ LE LIEN D'ACTIVATION — AFFICHÉ UNE SEULE FOIS ════════════════════
          ⚠️ PAS UNE ALERTE : une `alert()` disparaît au clic suivant et le lien
             serait perdu pour toujours. Cette fenêtre se ferme à la main. */}
      {lienEmis && (
        <div className="gss-modal-overlay" onClick={() => {}}>
          <div className="gss-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="gss-modal-header">
              <h2>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1A8754" strokeWidth="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                <div>
                  {lienEmis.titre}
                  <div className="gss-modal-subtitle">Lien d&rsquo;activation</div>
                </div>
              </h2>
            </div>
            <div style={{ padding: '1.5rem' }}>
              {lienEmis.message && (
                <div style={{ fontSize: 13, marginBottom: 12 }}>{lienEmis.message}</div>
              )}
              <div className="gss-alert gss-alert-warning" style={{ fontSize: 12, marginBottom: 12 }}>
                {lienEmis.avertissement} Transmettez-le maintenant à la personne concernée, en privé, jamais dans un groupe.
              </div>
              <div className="gss-field">
                <label className="gss-label">Lien à transmettre</label>
                {/* ⓘ Lecture seule et sélectionné au clic : on copie, on ne
                    retouche pas un jeton. */}
                <input className="gss-input" readOnly value={
                  /* ⓘ Le serveur rend un CHEMIN quand `APP_URL` n'est pas posée.
                     L'écran le complète avec sa propre origine plutôt que
                     d'afficher un lien tronqué que personne ne peut ouvrir. */
                  lienEmis.url && lienEmis.url.startsWith('http')
                    ? lienEmis.url : `${window.location.origin}${lienEmis.url}`
                } onFocus={(e) => e.target.select()} />
              </div>
              <div style={{ fontSize: 12, color: 'var(--gss-text-secondary)', marginTop: 8 }}>
                Expire le {lienEmis.expire_le ? new Date(lienEmis.expire_le).toLocaleString('fr-FR') : '—'}.
              </div>
            </div>
            <div className="gss-modal-actions">
              <button className="gss-btn-deploy" onClick={() => setLienEmis(null)}>
                J&rsquo;ai transmis le lien
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
